import { useState } from "react";

import { devices } from "../lib/api.js";
import { inMaintenance } from "../lib/freshness.js";

/**
 * Starting and ending maintenance on one device.
 *
 * Two shapes. Off, it is a small control in the header -- a length and a
 * button -- because starting maintenance should be quick. On, it becomes a
 * banner across the page, because the one thing nobody should miss while it
 * is on is that alerts for this device are off for *everyone*, including the
 * organizations it is shared with.
 *
 * Four hours is the default and "until ended" is last in the list, not first:
 * the dangerous failure of a mute switch is being left on.
 */

const LENGTHS: { label: string; hours: number | null }[] = [
  { label: "1 HOUR", hours: 1 },
  { label: "4 HOURS", hours: 4 },
  { label: "24 HOURS", hours: 24 },
  { label: "UNTIL ENDED", hours: null },
];

function remaining(until: string, now: number): string {
  const minutes = Math.max(0, Math.round((Date.parse(until) - now) / 60_000));
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

export function MaintenanceControl({
  deviceId,
  device,
  canWrite,
  now,
  onChange,
  variant,
}: {
  deviceId: string;
  device: {
    maintenance_started_at?: string | null;
    maintenance_until?: string | null;
    maintenance_by_email?: string | null;
  };
  canWrite: boolean;
  now: number;
  onChange: () => void;
  /** The header control, or the banner shown while it is on. */
  variant: "control" | "banner";
}) {
  const [length, setLength] = useState("1");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const active = inMaintenance(device, now);

  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      onChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (variant === "control") {
    if (active || !canWrite) return null;
    return (
      <span style={{ display: "inline-flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
        <select value={length} onChange={(e) => setLength(e.target.value)} style={select} title="How long maintenance lasts">
          {LENGTHS.map((l, i) => (
            <option key={l.label} value={String(i)}>{l.label}</option>
          ))}
        </select>
        <button
          disabled={busy}
          onClick={() => void run(() => devices.startMaintenance(deviceId, LENGTHS[Number(length)]!.hours))}
          title="Keep polling, but send no alerts to anyone"
          style={button}
        >
          [ MAINTENANCE ]
        </button>
        {error && <span style={{ color: "var(--status-critical)", fontSize: 11 }}>{error}</span>}
      </span>
    );
  }

  if (!active) return null;
  return (
    <div
      style={{
        border: "1px solid var(--status-maint)",
        background: "color-mix(in srgb, var(--status-maint) 12%, var(--surface-1))",
        padding: "10px 14px",
        marginBottom: 16,
        fontSize: 13,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
      }}
    >
      <div>
        <span style={{ color: "var(--status-maint)", letterSpacing: "0.1em" }}>MAINTENANCE</span>
        <span style={{ color: "var(--text-secondary)" }}>
          {" — "}
          {device.maintenance_until
            ? `ends ${new Date(device.maintenance_until).toLocaleString()} (in ${remaining(device.maintenance_until, now)})`
            : "until someone ends it"}
          {device.maintenance_by_email ? `, started by ${device.maintenance_by_email}` : ""}
        </span>
        <div style={{ color: "var(--text-muted)", fontSize: 11, marginTop: 3 }}>
          Still polled. No alerts go to anyone.
        </div>
        {error && <div style={{ color: "var(--status-critical)", fontSize: 11 }}>{`!! ${error}`}</div>}
      </div>
      {canWrite && (
        <button disabled={busy} onClick={() => void run(() => devices.endMaintenance(deviceId))} style={button}>
          [ END NOW ]
        </button>
      )}
    </div>
  );
}

const select: React.CSSProperties = {
  padding: "5px 7px", fontSize: 11, border: "1px solid var(--border)",
  background: "var(--surface-raised)", color: "var(--text-primary)", letterSpacing: "0.06em",
};
const button: React.CSSProperties = {
  background: "none", border: "1px solid var(--border)", padding: "5px 10px", fontSize: 12,
  color: "var(--text-secondary)", cursor: "pointer", letterSpacing: "0.08em",
  // A bracketed label folded across lines stops reading as a button at all.
  whiteSpace: "nowrap",
};
